import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, Search, Filter, Bug, Play, Zap, MessageSquare, ShoppingCart, ArrowLeft } from 'lucide-react';
import Cookies from 'js-cookie';
import MainLayout from '../Layout/MainLayout';

/**
 * SaleOrderAgentHistory - History page for NetSuite Sale Order workflow executions
 * Shows all past NetSuite sale order processing sessions with ability to view details
 */
const SaleOrderAgentHistory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get('agentId');
  const [loading, setLoading] = useState(true);
  const [workflowRuns, setWorkflowRuns] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, perPage: 20, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  // Fetch workflow runs
  const fetchWorkflowRuns = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = Cookies.get('sankalvax_token');
      const limit = pagination.perPage;
      const offset = (pagination.page - 1) * pagination.perPage;

      let url = `/api/sale/workflows/debug/executions?limit=${limit}&offset=${offset}`;
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch workflow runs');
      }

      const data = await response.json();
      setWorkflowRuns(data.executions || []);
      setPagination(prev => ({ ...prev, total: data.total || 0 }));

    } catch (err) {
      console.error('Error fetching workflow runs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflowRuns();
  }, [pagination.page, statusFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startedAt, completedAt) => {
    if (!startedAt) return 'N/A';
    if (!completedAt) return 'In progress';
    const start = new Date(startedAt);
    const end = new Date(completedAt);
    const durationMs = end - start;
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      completed: 'bg-green-100 text-green-800 border-green-200',
      running: 'bg-blue-100 text-blue-800 border-blue-200',
      awaiting_user: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      waiting_for_input: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      pending: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${statusStyles[status] || statusStyles.pending}`}>
        {status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  const filteredRuns = workflowRuns.filter(run => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      run.run_id?.toLowerCase().includes(query) ||
      run.workflow_name?.toLowerCase().includes(query) ||
      run.current_step?.toLowerCase().includes(query)
    );
  });

  const handleViewDebug = (runId) => {
    navigate(`/sale-debug?execution=${runId}`);
  };

  const handleContinueChat = (runId) => {
    navigate(`/sale-order-agent?runId=${runId}`);
  };

  const handleViewChat = (runId) => {
    // Open chat in view-only mode for completed workflows
    navigate(`/sale-order-agent?runId=${runId}&viewOnly=true`);
  };

  const handleStartNew = () => {
    navigate('/sale-order-agent');
  };

  return (
    <MainLayout title="NetSuite Sale Order History">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            {/* Back to Agent button - only show if agentId exists */}
            {agentId && (
              <button
                onClick={() => navigate(`/agents/${agentId}`)}
                className="p-2.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                title="Back to Agent"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <div className="relative">
                  <ShoppingCart className="w-7 h-7 text-orange-600" />
                  <Zap className="w-3 h-3 text-yellow-500 absolute -bottom-0.5 -right-0.5" />
                </div>
                NetSuite Sale Order History
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                View and manage past NetSuite sale order processing sessions
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={fetchWorkflowRuns}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleStartNew}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:shadow-lg transition-all"
            >
              <Play className="w-4 h-4" />
              New Session
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by run ID, workflow name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="running">Running</option>
              <option value="awaiting_user">Awaiting User</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-orange-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredRuns.length === 0 && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Sale Orders Found</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Start a new NetSuite sale order workflow to see history here.
            </p>
            <button
              onClick={handleStartNew}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:shadow-lg transition-all"
            >
              <Play className="w-4 h-4" />
              Start New Session
            </button>
          </div>
        )}

        {/* Workflow Runs List */}
        {!loading && filteredRuns.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-900/50 dark:to-gray-900/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider">
                      Date / Run ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider">
                      Current Step
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider">
                      Steps
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider">
                      API Calls
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider">
                      Tokens
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredRuns.map((run) => (
                    <tr
                      key={run.run_id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                            <ShoppingCart className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatDate(run.started_at)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                              {run.run_id?.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(run.status)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 dark:text-gray-300 font-mono">
                          {run.current_step || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {run.step_count || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                          {run.api_call_count || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-sm font-mono text-gray-600 dark:text-gray-300">
                          {run.total_tokens?.toLocaleString() || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {formatDuration(run.started_at, run.completed_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Continue Chat button - only for active sessions */}
                          {run.status !== 'completed' && run.status !== 'failed' && (
                            <button
                              onClick={() => handleContinueChat(run.run_id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-medium rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                              title="Continue this session"
                            >
                              <Play className="w-3.5 h-3.5" />
                              Continue
                            </button>
                          )}

                          {/* View Chat button - for completed/failed workflows */}
                          {(run.status === 'completed' || run.status === 'failed') && (
                            <button
                              onClick={() => handleViewChat(run.run_id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                              title="View chat conversation"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                              View Chat
                            </button>
                          )}

                          {/* View Debug button */}
                          <button
                            onClick={() => handleViewDebug(run.run_id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-medium rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
                            title="View debug details"
                          >
                            <Bug className="w-3.5 h-3.5" />
                            Debug
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > pagination.perPage && (
              <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {((pagination.page - 1) * pagination.perPage) + 1} to{' '}
                  {Math.min(pagination.page * pagination.perPage, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page <= 1}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page * pagination.perPage >= pagination.total}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default SaleOrderAgentHistory;
